<template>
  <div @click="triggerClick" class="trigger_value" :class="{ focus: focus, selected: selected, empty: isEmpty, readonly: readonly, large: large }">
    <div v-if="label" class="slot_label">{{ label }}</div>
    <div class="key_display">
      <template v-if="isEmpty">
        {{ t('unset') }}
      </template>
      <template v-else>
        <span v-if="isCombinationKey(safeVks)" class="is_plus">{{ $t('action_set_combination_key') }}</span>
        <span class="compact_text" v-else :style="{ fontSize: getTextSize(safeVks) }">
          {{ compactText }}
        </span>
      </template>
    </div>
    <div v-if="showEdit" class="edit_hint">
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" /></svg>
    </div>

    <template v-if="isCombinationKey(safeVks)">
      <img v-if="selected" class="trigger_star" src="@/assets/img/star.svg" />
      <trigger_tips :datas="safeVks" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { VK } from '@/define/define'
import trigger_tips from './trigger_tips.vue'
import { BaseTabs } from '@/service/driver/action_set'
import { kbVKTexture, mouseVKs } from '@/define/vkcode'
import i18n from '@/languages/i18n'
import { adjustFontSize } from '@/tool/utility'
import { computed } from 'vue'
import { cleanVksForDisplay } from './action_config'
const { t } = i18n.global

const props = defineProps<{
  focus?: boolean
  selected?: boolean
  tab: BaseTabs
  vks: VK[]
  label?: string
  showEdit?: boolean
  readonly?: boolean
  large?: boolean
}>()
const emit = defineEmits(['triggerClick'])

// 统一通过 cleaning 逻辑处理显示数据，确保与当前模式能力同步
const safeVks = computed(() => cleanVksForDisplay(props.vks))
const filteredVks = computed(() => safeVks.value.filter((e) => e > 0))

const triggerClick = () => {
  if (props.readonly) return
  emit('triggerClick')
}

const isEmpty = computed(() => {
  return filteredVks.value.length === 0
})

const getSingleVKText = (key: VK) => {
  if (key == 0x00) return ''
  if (mouseVKs[key]) return t(mouseVKs[key])
  return kbVKTexture[key] || '?'
}

const compactText = computed(() => {
  if (isEmpty.value) return ''
  // 选中的第一个有效按键文字
  let firstKeyText = getSingleVKText(filteredVks.value[0])
  // 如果是组合键，只显示第一个按键前缀
  return firstKeyText
})

const getText = (keys: VK[]): string => {
  const list = (keys || []).filter((e) => e > 0)
  if (list.length === 0) return ''
  const baseText = getSingleVKText(list[0])
  if (list.length > 1) {
    return `${baseText}+`
  }
  return baseText
}

const getTextSize = (keys: VK[]): string => {
  const text = getText(keys)
  if (!text) return '14px'
  const isLarge = props.large

  // 1. 计算视觉权重长度 (汉字计 2, 字母/符号计 1)
  const visualLength = text.split('').reduce((acc, char) => {
    return acc + (/[^\x00-\xff]/.test(char) ? 2 : 1)
  }, 0)

  // 2. 根据视觉长度初步设定基础字号
  let baseSize = isLarge ? 22 : 13

  if (isLarge) {
    // 看板模式 (容器有效宽度约 100px)
    if (visualLength > 10) {
      // 约 5 个汉字以上
      baseSize = 15
    } else if (visualLength > 6) {
      // 约 3-4 个汉字
      baseSize = 18
    }
  } else {
    // 标准模式 (容器宽度约 70px)
    if (visualLength > 8) {
      baseSize = 11
    }
  }

  // 3. 设定逻辑目标宽度进行二次微调
  const targetWidth = isLarge ? 95 : 72
  return `${adjustFontSize(text, targetWidth, baseSize)}px`
}

const isCombinationKey = (keys: VK[]) => {
  return filteredVks.value.length > 1
}
const combinationVks = computed(() => filteredVks.value)
</script>

<style lang="less" scoped>
.trigger_star {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 14px;
  height: 14px;
  filter: drop-shadow(0 2px 4px rgba(var(--color-brand-1-rgb), 0.3));
}

.key_display {
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1.2;
  white-space: normal; /* 允许在单词间换行 */
  word-break: keep-all; /* 确保像 Scroll 这样的单词不被拆分成多行 */
  overflow-wrap: break-word; /* 如果单词确实极长，仍然可以换行以防超出 */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 100%;
  overflow: hidden;

  .compact_text {
    display: block;
    white-space: normal;
    overflow: hidden;
    max-width: 100%;
    width: 100%;

    .is_plus {
      color: inherit;
      opacity: 0.6;
      font-size: 0.9em;
      margin-left: 1px;
    }
  }
}

.trigger_value {
  width: 100%;
  min-height: 38px;
  min-width: 98px;
  background: var(--color-white);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: var(--action-btn-radius);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  cursor: pointer;
  position: relative;
  transition: var(--transition-base);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);

  &:hover {
    .trigger_tip {
      display: flex;
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  &.large {
    height: 80px;
    padding: 12px 14px;
    border-radius: var(--radius-l);
    border-color: rgba(0, 0, 0, 0.05);

    .slot_label {
      font-size: 12px;
      margin-bottom: 2px;
      line-height: 1.2;
    }
    .key_display {
      font-weight: 900;
      min-height: 28px;
    }
    .edit_hint {
      top: 10px;
      right: 10px;
      width: 14px;
      height: 14px;
    }
  }

  .slot_label {
    font-size: 12px;
    color: var(--color-text-hint);
    font-weight: 800;
    text-transform: uppercase;
    margin-bottom: 2px;
    line-height: 1;
  }

  .key_display {
    font-weight: 700;
    color: var(--color-dark);
    line-height: 1.2;
  }

  .edit_hint {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 10px;
    height: 10px;
    color: var(--color-text-hint);
    opacity: 0.3;
  }

  &.focus {
    border-color: var(--brand-color-pure);
    background: #fff;
    box-shadow: 0 0 0 1px var(--brand-color-pure);
    .key_display {
      color: var(--brand-color-pure);
    }
  }

  &.selected {
    background: var(--brand-color);
    border-color: transparent;
    box-shadow: 0 8px 20px rgba(255, 124, 0, 0.2);
    .key_display {
      color: #fff;
    }
    .slot_label {
      color: rgba(255, 255, 255, 0.8);
    }
    .edit_hint {
      color: #fff;
      opacity: 0.6;
    }
  }

  &.empty {
    .key_display {
      color: var(--color-text-hint);
      font-size: 12px !important;
      font-weight: 600;
    }
  }

  &:hover:not(.focus):not(.selected) {
    background: #fff;
    border-color: var(--brand-color-pure);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }

  .trigger_tip {
    transition: opacity 0.3s;
  }
}
</style>
